import { useState, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Heart, Shield, TrendingUp, Clock, AlertTriangle, Package, CheckCircle } from 'lucide-react';

const ProductImageGallery = ({ 
  mainImage, 
  images = [], 
  productName,
  isWishlisted,
  onWishlistToggle,
  stockStatus,
  warranty,
  avgRating,
  ratingCount
}) => {
  // Combine main image with additional images, removing duplicates
  const allImages = useMemo(() => {
    const imageSet = new Set([mainImage, ...images]);
    return Array.from(imageSet).filter(Boolean).slice(0, 5);
  }, [mainImage, images]);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const mainImageRef = useRef(null);

  const currentImage = allImages[selectedIndex] || mainImage;

  const handlePrevious = () => {
    setSelectedIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const handleNext = () => {
    setSelectedIndex((prev) => (prev + 1) % allImages.length);
  };

  // Auto-slide on thumbnail hover (Noon style)
  const handleThumbnailHover = (index) => {
    setSelectedIndex(index);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
      {/* Main Image Container */}
      <div 
        ref={mainImageRef}
        className="relative aspect-[4/5] w-full bg-slate-50 overflow-hidden"
      >
        {/* Main Image with opacity fade transition */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentImage}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full h-full flex items-center justify-center p-2 sm:p-3"
          >
            <img
              src={currentImage}
              alt={productName}
              className="max-w-full max-h-full object-contain mx-auto"
              draggable={false}
              decoding="async"
              onError={(e) => {
                if (!e.target.dataset.fallbackApplied) {
                  e.target.dataset.fallbackApplied = '1';
                  e.target.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 400'><rect width='400' height='400' fill='%23f1f5f9'/><g fill='%2394a3b8'><circle cx='200' cy='170' r='40'/><path d='M120 280 L160 220 L200 250 L240 200 L280 280 Z'/></g><text x='200' y='340' font-family='system-ui' font-size='18' fill='%2364748b' text-anchor='middle'>Image coming soon</text></svg>";
                }
              }}
            />
          </motion.div>
        </AnimatePresence>

        {/* Navigation Arrows (always visible when multiple images, desktop + mobile) */}
        {allImages.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); handlePrevious(); }}
              className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-11 sm:h-11 bg-white/95 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center hover:bg-white hover:scale-105 active:scale-95 transition-all z-20"
              aria-label="Previous image"
              data-testid="gallery-prev-btn"
            >
              <ChevronLeft className="w-5 h-5 text-slate-700" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleNext(); }}
              className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-11 sm:h-11 bg-white/95 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center hover:bg-white hover:scale-105 active:scale-95 transition-all z-20"
              aria-label="Next image"
              data-testid="gallery-next-btn"
            >
              <ChevronRight className="w-5 h-5 text-slate-700" />
            </button>
          </>
        )}

        {/* Wishlist Button */}
        <button
          onClick={onWishlistToggle}
          className="absolute top-4 right-4 p-3 bg-white rounded-full shadow-lg hover:scale-110 transition-transform z-10"
          data-testid="wishlist-btn"
        >
          <Heart
            className={`w-6 h-6 transition-colors ${
              isWishlisted ? 'text-red-500 fill-red-500' : 'text-slate-400'
            }`}
          />
        </button>

        {/* Image Counter */}
        {allImages.length > 1 && (
          <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded">
            {selectedIndex + 1} / {allImages.length}
          </div>
        )}
      </div>

      {/* Badges Strip (below image, above thumbnails — no overlap on product) */}
      {(stockStatus?.urgent ||
        (ratingCount >= 5 && avgRating >= 4)) && (
        <div className="flex flex-wrap items-center justify-center gap-2 px-3 py-3 border-t border-slate-100 bg-white">
          {stockStatus?.urgent && (
            <span
              className={`${stockStatus.bg} ${stockStatus.color} px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5`}
              data-testid="badge-stock"
            >
              {stockStatus.icon && <stockStatus.icon className="w-3.5 h-3.5" />}
              {stockStatus.label}
            </span>
          )}
          {ratingCount >= 5 && avgRating >= 4 && (
            <span
              className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5"
              data-testid="badge-trending"
            >
              <TrendingUp className="w-3.5 h-3.5" />
              Trending
            </span>
          )}
        </div>
      )}

      {/* Mobile: dot indicators (Noon-style); Desktop: clickable thumbnails */}
      {allImages.length > 1 && (
        <>
          {/* Dots (mobile only) */}
          <div className="flex sm:hidden items-center justify-center gap-1.5 py-3 border-t border-slate-100">
            {allImages.map((_img, idx) => (
              <button
                key={`dot-${idx}`}
                onClick={() => setSelectedIndex(idx)}
                aria-label={`Show image ${idx + 1}`}
                data-testid={`gallery-dot-${idx}`}
                className={`transition-all rounded-full ${
                  idx === selectedIndex
                    ? 'w-6 h-2 bg-[#0047AB]'
                    : 'w-2 h-2 bg-slate-300 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>

          {/* Thumbnails (desktop only) */}
          <div className="hidden sm:block p-3 border-t border-slate-100">
            <div className="flex gap-2 justify-start sm:justify-center overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {allImages.map((img, idx) => (
                <button
                  key={`thumb-${idx}-${img.substring(img.lastIndexOf('/') + 1, img.lastIndexOf('/') + 10)}`}
                  onClick={() => setSelectedIndex(idx)}
                  onMouseEnter={() => handleThumbnailHover(idx)}
                  className={`
                    relative shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden border-2 transition-all
                    ${idx === selectedIndex 
                      ? 'border-[#0047AB] ring-2 ring-[#0047AB]/20' 
                      : 'border-slate-200 hover:border-slate-300'
                    }
                  `}
                  aria-label={`Show image ${idx + 1}`}
                  data-testid={`thumbnail-${idx}`}
                >
                  <img
                    src={img}
                    alt={`${productName} - View ${idx + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  {idx === selectedIndex && (
                    <div className="absolute inset-0 bg-[#0047AB]/10" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductImageGallery;
